import type { Trade } from '../types';
import { sortTradesNewestFirst } from '../lib/trades/authoritativeTradeData';
import { groupExecutionsToTrades, type Execution as CanonicalExecution } from '../lib/import-engine/universalImporter';
import { resolveExecutionRows } from '../lib/trades/resolveExecutionRows';
import {
  buildNewYorkTimestamp,
  getRecentUsEquitySessionDateKeys,
  getUsEquityMarketDateStatus,
} from '../lib/market/usEquityMarketCalendar';

export type DemoTenantId = string;

type DemoTrade = Trade & Record<string, any>;

type DemoTradePlan = {
  symbol: string;
  side: 'LONG' | 'SHORT';
  hour: number;
  minute: number;
  durationMinutes: number;
  quantity: number;
  entryPrice: number;
  pnl: number;
  notes: string;
  tags: string[];
  market: 'STOCKS';
  companyName: string;
  strategy: string;
  atr?: number;
  relativeVolatility?: number;
  status?: string;
};

const TRADE_COUNTS_BY_DAY = [4, 5, 3, 6, 4, 7, 2] as const;

const round2 = (value: number) => Math.round(value * 100) / 100;

function allocateCosts(total: number, count: number): number[] {
  if (count <= 0) return [];
  const base = round2(total / count);
  const values = Array.from({ length: count }, () => base);
  values[count - 1] = round2(total - values.slice(0, -1).reduce((sum, value) => sum + value, 0));
  return values;
}

function buildCanonicalDemoExecutions(
  tenantId: DemoTenantId,
  dateKey: string,
  id: number,
  plan: DemoTradePlan,
  totalCosts: number,
): CanonicalExecution[] {
  const isOpen = String(plan.status ?? '').trim().toLowerCase() === 'open';
  const openTimestampMs = buildNewYorkTimestamp(dateKey, plan.hour, plan.minute);
  const closeTimestampMs = openTimestampMs + plan.durationMinutes * 60 * 1000;
  const rowCount = isOpen ? 1 : (plan.quantity > 50 ? 3 : 2);
  const costs = allocateCosts(totalCosts, rowCount);
  const exitDirection = plan.side === 'LONG' ? 1 : -1;
  const grossPnl = plan.pnl + totalCosts;
  const exitPrice = plan.entryPrice + exitDirection * (grossPnl / Math.max(1, plan.quantity));
  const openingSide: CanonicalExecution['side'] = plan.side === 'LONG' ? 'buy' : 'sell';
  const closingSide: CanonicalExecution['side'] = openingSide === 'buy' ? 'sell' : 'buy';
  const orderId = `demo-${tenantId}-order-${id}`;
  const executions: CanonicalExecution[] = [{
    id: `${orderId}-1`,
    // A demo plan represents one synthetic order lifecycle. Keep its entry
    // and exit fills under the same order id so strict importer grouping
    // does not intentionally isolate the demo legs.
    orderId,
    timestamp: openTimestampMs,
    symbol: plan.symbol,
    side: openingSide,
    qty: plan.quantity,
    price: plan.entryPrice,
    commission: costs[0],
    notes: plan.notes,
    tags: plan.tags,
    assetClass: 'STOCKS',
  }];

  if (isOpen) return executions;

  const closeRowCount = rowCount - 1;
  const firstCloseQty = closeRowCount === 2 ? Math.floor(plan.quantity / 2) : plan.quantity;
  const closeQuantities = closeRowCount === 2
    ? [firstCloseQty, plan.quantity - firstCloseQty]
    : [plan.quantity];

  closeQuantities.forEach((qty, index) => {
    executions.push({
      id: `${orderId}-${index + 2}`,
      orderId,
      timestamp: closeRowCount === 1
        ? closeTimestampMs
        : closeTimestampMs - ((closeRowCount - index - 1) * 60_000),
      symbol: plan.symbol,
      side: closingSide,
      qty,
      price: exitPrice,
      commission: costs[index + 1],
      brokerReportedPnl: index === closeQuantities.length - 1 ? plan.pnl : undefined,
      notes: plan.notes,
      tags: plan.tags,
      assetClass: 'STOCKS',
    });
  });

  return executions;
}

export function getDemoTradeConsistencyErrors(trade: Record<string, any>): string[] {
  const errors: string[] = [];
  const executions = Array.isArray(trade?.executionsList)
    ? trade.executionsList
    : (Array.isArray(trade?.executions) ? trade.executions : []);
  if (executions.length === 0) errors.push('canonical executions are missing');

  const resolvedRows = resolveExecutionRows(executions);
  const reconstructedGross = round2(resolvedRows.reduce((sum, row) => sum + row.rowRealizedGross, 0));
  const reconstructedNet = round2(resolvedRows.reduce((sum, row) => sum + row.rowRealizedNet, 0));
  const executionCosts = round2(executions.reduce((sum: number, row: Record<string, any>) => (
    sum + Number(row.commissionAndFees ?? row.commissionsAndFees ?? row.commission ?? 0)
  ), 0));
  const isOpen = String(trade?.status ?? '').trim().toLowerCase() === 'open';

  if (!isOpen && Math.abs(reconstructedGross - Number(trade?.grossPnl)) > 0.01) {
    errors.push(`gross P&L ${trade?.grossPnl} does not match executions ${reconstructedGross}`);
  }
  if (!isOpen && Math.abs(reconstructedNet - Number(trade?.netPnl ?? trade?.pnl)) > 0.01) {
    errors.push(`net P&L ${trade?.netPnl ?? trade?.pnl} does not match executions ${reconstructedNet}`);
  }
  if (Math.abs(executionCosts - Number(trade?.commissionAndFees)) > 0.01) {
    errors.push(`fees ${trade?.commissionAndFees} do not match executions ${executionCosts}`);
  }
  if (Number(trade?.executionCount) !== executions.length) {
    errors.push(`execution count ${trade?.executionCount} does not match executions ${executions.length}`);
  }
  const dateStatus = getUsEquityMarketDateStatus(String(trade?.date ?? ''), new Date(Number(trade?.openTimestampMs) + 24 * 60 * 60 * 1000));
  if (!dateStatus.valid) errors.push(dateStatus.reason ?? 'invalid U.S. equity market date');
  return errors;
}

function makeDemoTrade(tenantId: DemoTenantId, dateKey: string, dayIndex: number, sequence: number, plan: DemoTradePlan): DemoTrade {
  const id = dayIndex * 10 + sequence + 1;
  const tenantPrefix = tenantId === 'warrior-trading' ? 'WT' : 'TP';
  const commission = 1.9;
  const fees = 0.55;
  const commissionAndFees = round2(commission + fees);
  const canonicalExecutions = buildCanonicalDemoExecutions(tenantId, dateKey, id, plan, commissionAndFees);
  const aggregated = groupExecutionsToTrades(canonicalExecutions)[0];
  if (!aggregated) throw new Error(`Unable to aggregate demo trade ${tenantId}:${id}`);

  const openTimestampMs = aggregated.openTimestampMs;
  const closeTimestampMs = aggregated.closeTimestampMs ?? null;
  const initialRisk = Math.max(35, Math.round(Math.abs(plan.pnl) * (plan.pnl >= 0 ? 0.54 : 0.72)));
  const assetClass = 'STK';
  const isOpen = String(plan.status ?? '').trim().toLowerCase() === 'open';
  const result = isOpen ? 'open' : plan.pnl > 5 ? 'win' : plan.pnl < -5 ? 'loss' : 'breakeven';
  const maeMagnitude = round2(Math.max(8, plan.pnl >= 0 ? Math.abs(plan.pnl) * 0.28 : Math.abs(plan.pnl) * 1.16));
  const mfeValue = round2(Math.max(10, plan.pnl >= 0 ? Math.abs(plan.pnl) * 1.22 : Math.abs(plan.pnl) * 0.42));
  const maeValue = -maeMagnitude;
  const maeNet = round2(maeValue - commissionAndFees);
  const mfeNet = round2(mfeValue - commissionAndFees);
  const realizedPnl = isOpen ? null : round2(Number(aggregated.calculatedPnl));
  const realizedGrossPnl = isOpen ? null : round2(Number(aggregated.calculatedPnl) + commissionAndFees);
  const resolvedExitPrice = isOpen ? undefined : aggregated.exitPrice;
  const resolvedCloseTimestampMs = isOpen ? null : closeTimestampMs;
  const resolvedDuration = isOpen ? null : plan.durationMinutes;
  const openMovementPercent = isOpen ? null : round2((Number(resolvedExitPrice) - plan.entryPrice) / plan.entryPrice * 100);
  const openAtrPercent = isOpen ? null : (plan.atr ? round2(((Number(resolvedExitPrice) - plan.entryPrice) / plan.atr) * 100) : null);
  const notionalVolume = round2(plan.entryPrice * aggregated.volume);

  const trade = {
    ...aggregated,
    id,
    tradeId: `Demo_${tenantPrefix}_${String(id).padStart(3, '0')}`,
    storageId: `demo-${tenantId}-${id}`,
    date: dateKey,
    symbol: plan.symbol,
    volume: aggregated.volume,
    qty: aggregated.quantity,
    quantity: aggregated.quantity,
    shares: aggregated.volume,
    executions: canonicalExecutions,
    executionsList: canonicalExecutions,
    executionCount: canonicalExecutions.length,
    tradedSize: aggregated.volume,
    maxPositionSize: aggregated.quantity,
    peakPositionSize: aggregated.quantity,
    pnl: realizedPnl,
    netPnl: realizedPnl,
    net_pnl: realizedPnl,
    grossPnl: realizedGrossPnl,
    gross_pnl: realizedGrossPnl,
    realizedPnl,
    realizedNetPnl: realizedPnl,
    realizedGrossPnl,
    status: isOpen ? 'open' : 'closed',
    side: plan.side,
    notes: plan.notes,
    tags: ['Demo data', ...plan.tags],
    entryPrice: plan.entryPrice,
    exitPrice: resolvedExitPrice,
    duration: resolvedDuration,
    durationMinutes: resolvedDuration,
    account: tenantId === 'warrior-trading' ? 'WT-DEMO-CHALLENGE' : 'TP-DEMO-ACCOUNT',
    broker: 'Demo account preview',
    source: 'demo',
    sourceType: 'demo',
    isDemo: true,
    demoTenantId: tenantId,
    demoResult: result,
    strategy: plan.strategy,
    calculatedPnl: realizedPnl,
    brokerReportedPnl: realizedPnl,
    openTimestampMs,
    closeTimestampMs: resolvedCloseTimestampMs,
    timestamp: openTimestampMs,
    entryTime: openTimestampMs,
    entryTimestamp: openTimestampMs,
    executedAt: openTimestampMs,
    openTime: openTimestampMs,
    exitTime: resolvedCloseTimestampMs,
    exitTimestamp: resolvedCloseTimestampMs,
    closeTime: resolvedCloseTimestampMs,
    closedAt: resolvedCloseTimestampMs,
    market: plan.market,
    marketType: plan.market,
    assetClass,
    category: plan.market,
    instrumentType: assetClass,
    companyName: plan.companyName,
    description: plan.companyName,
    commission,
    fees,
    commissionAndFees,
    totalFees: commissionAndFees,
    feesAndCommissions: commissionAndFees,
    initialRisk,
    initialRiskAmount: initialRisk,
    riskAmount: initialRisk,
    pnlR: isOpen ? null : round2(plan.pnl / initialRisk),
    rMultiple: isOpen ? null : round2(plan.pnl / initialRisk),
    mae: maeValue,
    mfe: mfeValue,
    maeGross: maeValue,
    mfeGross: mfeValue,
    maeNet,
    mfeNet,
    positionMAE: maeValue,
    positionMFE: mfeValue,
    maxAdverseExcursion: maeValue,
    maxFavorableExcursion: mfeValue,
    deterministicMae: maeValue,
    deterministicMfe: mfeValue,
    pnlPercentEquities: isOpen ? null : round2((plan.pnl / notionalVolume) * 100),
    pnlTicksGrossFutures: null,
    pnlTicksPerContractGrossFutures: null,
    relativeVolatility: plan.relativeVolatility ?? 96,
    rvol: plan.relativeVolatility ?? 96,
    movementPercent: openMovementPercent,
    priceMovementPercent: openMovementPercent,
    entryPercentageOfAtr: openAtrPercent,
    entryPctAtr: openAtrPercent,
    symbolAtr: plan.atr ?? null,
    atr: plan.atr ?? null,
    openingGapPercent: 0.42,
    openGapPercent: 0.42,
    inTradePriceRange: isOpen ? null : (plan.atr ? round2(Math.abs(Number(resolvedExitPrice) - plan.entryPrice) / plan.atr) : null),
    priceRangeDuringTrade: isOpen ? null : (plan.atr ? round2(Math.abs(Number(resolvedExitPrice) - plan.entryPrice) / plan.atr) : null),
    notionalVolume,
    dollarVolume: notionalVolume,
    tradeValue: notionalVolume,
  } as unknown as DemoTrade;

  const consistencyErrors = getDemoTradeConsistencyErrors(trade);
  if (consistencyErrors.length > 0) {
    throw new Error(`Invalid demo trade ${trade.tradeId}: ${consistencyErrors.join('; ')}`);
  }
  return trade;
}

const TRADE_PROOF_PLANS: DemoTradePlan[] = [
  { symbol: 'AAPL', side: 'LONG', hour: 9, minute: 38, durationMinutes: 34, quantity: 80, entryPrice: 213.4, pnl: 176, notes: 'Opening range reclaim held above VWAP with steady volume.', tags: ['Opening Drive', 'VWAP'], market: 'STOCKS', companyName: 'Apple Inc.', strategy: 'Opening Drive', atr: 4.8, relativeVolatility: 108 },
  { symbol: 'MSFT', side: 'SHORT', hour: 10, minute: 18, durationMinutes: 41, quantity: 45, entryPrice: 428.2, pnl: -84, notes: 'Failed continuation after the first pullback lost breadth support.', tags: ['Pullback', 'Risk Cut'], market: 'STOCKS', companyName: 'Microsoft Corporation', strategy: 'Trend Pullback', atr: 6.4, relativeVolatility: 82 },
  { symbol: 'AMD', side: 'LONG', hour: 10, minute: 41, durationMinutes: 26, quantity: 70, entryPrice: 171.5, pnl: 112, notes: 'Reduced size continuation after VWAP support held.', tags: ['Scalp', 'VWAP Support'], market: 'STOCKS', companyName: 'Advanced Micro Devices', strategy: 'VWAP Pullback', atr: 5.4, relativeVolatility: 114, status: 'OPEN' },
  { symbol: 'TSLA', side: 'LONG', hour: 13, minute: 18, durationMinutes: 32, quantity: 60, entryPrice: 184.8, pnl: 186, notes: 'Equity scalp followed a clean reclaim of the morning pivot.', tags: ['Momentum', 'Equity Scalp', 'Long Term'], market: 'STOCKS', companyName: 'Tesla Inc.', strategy: 'Pivot Reclaim', atr: 7.6, relativeVolatility: 152, status: 'OPEN' },
  { symbol: 'TSLA', side: 'SHORT', hour: 9, minute: 46, durationMinutes: 24, quantity: 55, entryPrice: 184.6, pnl: 198, notes: 'Premarket support failed cleanly after the first lower high.', tags: ['Fade', 'Momentum'], market: 'STOCKS', companyName: 'Tesla Inc.', strategy: 'Opening Fade', atr: 7.8, relativeVolatility: 161 },
  { symbol: 'GOOGL', side: 'LONG', hour: 10, minute: 32, durationMinutes: 37, quantity: 45, entryPrice: 172.3, pnl: -122, notes: 'Breakout attempt stalled at prior-day resistance.', tags: ['Breakout', 'Discipline'], market: 'STOCKS', companyName: 'Alphabet Inc.', strategy: 'Failed Breakout', atr: 3.2, relativeVolatility: 91 },
  { symbol: 'PLTR', side: 'LONG', hour: 11, minute: 51, durationMinutes: 31, quantity: 200, entryPrice: 23.25, pnl: 92, notes: 'Strong pullback hold above VWAP with rising afternoon volume.', tags: ['Tech', 'VWAP Hold'], market: 'STOCKS', companyName: 'Palantir Technologies', strategy: 'VWAP Pullback', atr: 1.2, relativeVolatility: 112 },
  { symbol: 'AAPL', side: 'SHORT', hour: 14, minute: 8, durationMinutes: 19, quantity: 70, entryPrice: 215.2, pnl: -64, notes: 'Late-day fade did not accelerate and was closed quickly.', tags: ['Afternoon', 'Risk Cut'], market: 'STOCKS', companyName: 'Apple Inc.', strategy: 'Late Fade', atr: 4.6, relativeVolatility: 88 },
  { symbol: 'MSFT', side: 'LONG', hour: 9, minute: 42, durationMinutes: 48, quantity: 40, entryPrice: 426.5, pnl: 134, notes: 'Clean 20 EMA hold after the opening drive.', tags: ['Large Cap', 'EMA Hold'], market: 'STOCKS', companyName: 'Microsoft Corporation', strategy: 'Trend Pullback', atr: 6.1, relativeVolatility: 96 },
  { symbol: 'NVDA', side: 'SHORT', hour: 10, minute: 27, durationMinutes: 23, quantity: 14, entryPrice: 918.7, pnl: -280, notes: 'Countertrend short was too early against strong tape.', tags: ['Countertrend', 'Semis'], market: 'STOCKS', companyName: 'NVIDIA Corporation', strategy: 'Countertrend Fade', atr: 22.2, relativeVolatility: 151 },
  { symbol: 'CRM', side: 'SHORT', hour: 13, minute: 5, durationMinutes: 42, quantity: 75, entryPrice: 235.4, pnl: 151, notes: 'Range rejection at morning high resolved lower into VWAP.', tags: ['SaaS', 'Range Rejection'], market: 'STOCKS', companyName: 'Salesforce Inc.', strategy: 'Range Rejection', atr: 5.5, relativeVolatility: 79 },
  { symbol: 'TSLA', side: 'LONG', hour: 14, minute: 44, durationMinutes: 26, quantity: 45, entryPrice: 182.9, pnl: 78, notes: 'Small afternoon continuation after reclaiming VWAP.', tags: ['Afternoon', 'VWAP Reclaim'], market: 'STOCKS', companyName: 'Tesla Inc.', strategy: 'VWAP Reclaim', atr: 7.5, relativeVolatility: 133 },
  { symbol: 'AVGO', side: 'SHORT', hour: 9, minute: 35, durationMinutes: 33, quantity: 15, entryPrice: 1461.8, pnl: 118, notes: 'Opening flush followed weak megacap breadth.', tags: ['Opening Drive', 'Semis'], market: 'STOCKS', companyName: 'Broadcom Inc.', strategy: 'Opening Fade', atr: 28.5, relativeVolatility: 103 },
  { symbol: 'COIN', side: 'SHORT', hour: 10, minute: 12, durationMinutes: 20, quantity: 30, entryPrice: 226.5, pnl: -38, notes: 'Quick scalp failed as buyers defended the midpoint.', tags: ['Tech', 'Scalp'], market: 'STOCKS', companyName: 'Coinbase Global Inc.', strategy: 'Mean Reversion', atr: 8.5, relativeVolatility: 109 },
  { symbol: 'AAPL', side: 'LONG', hour: 11, minute: 19, durationMinutes: 54, quantity: 65, entryPrice: 212.8, pnl: 96, notes: 'Measured continuation after consolidation above VWAP.', tags: ['Continuation', 'VWAP'], market: 'STOCKS', companyName: 'Apple Inc.', strategy: 'VWAP Continuation', atr: 4.4, relativeVolatility: 92 },
  { symbol: 'MSFT', side: 'LONG', hour: 12, minute: 46, durationMinutes: 35, quantity: 35, entryPrice: 427.9, pnl: 0, notes: 'Flat exit when the setup stopped progressing.', tags: ['Scratch', 'Discipline'], market: 'STOCKS', companyName: 'Microsoft Corporation', strategy: 'Lunch Breakout', atr: 6.3, relativeVolatility: 85 },
  { symbol: 'NVDA', side: 'LONG', hour: 13, minute: 37, durationMinutes: 47, quantity: 16, entryPrice: 909.1, pnl: 312, notes: 'Best trade of the week after semis broke from a two-hour base.', tags: ['Breakout', 'Semis', 'Long Term'], market: 'STOCKS', companyName: 'NVIDIA Corporation', strategy: 'Base Breakout', atr: 20.9, relativeVolatility: 148 },
  { symbol: 'SMCI', side: 'LONG', hour: 14, minute: 28, durationMinutes: 22, quantity: 20, entryPrice: 833.6, pnl: -54, notes: 'Late continuation failed into supply and was cut before close.', tags: ['Late Day', 'Risk Cut'], market: 'STOCKS', companyName: 'Super Micro Computer Inc.', strategy: 'Late Continuation', atr: 45.0, relativeVolatility: 81 },
];

const WARRIOR_TRADING_PLANS: DemoTradePlan[] = [
  { symbol: 'TSLA', side: 'LONG', hour: 9, minute: 34, durationMinutes: 18, quantity: 80, entryPrice: 188.25, pnl: 225, notes: 'Opening drive held above VWAP and cleared premarket high.', tags: ['Opening Drive', 'VWAP', 'Long Term'], market: 'STOCKS', companyName: 'Tesla Inc.', strategy: 'Opening Drive', atr: 7.6, relativeVolatility: 121 },
  { symbol: 'NVDA', side: 'SHORT', hour: 10, minute: 4, durationMinutes: 15, quantity: 15, entryPrice: 912.5, pnl: -170, notes: 'Short entry chased the flush and was stopped on the reclaim.', tags: ['Risk Cut', 'Semis'], market: 'STOCKS', companyName: 'NVIDIA Corporation', strategy: 'Failed Breakdown', atr: 21.0, relativeVolatility: 138 },
  { symbol: 'AMD', side: 'LONG', hour: 10, minute: 41, durationMinutes: 26, quantity: 70, entryPrice: 171.5, pnl: 112, notes: 'Reduced size continuation after VWAP support held.', tags: ['Scalp', 'VWAP Support'], market: 'STOCKS', companyName: 'Advanced Micro Devices', strategy: 'VWAP Pullback', atr: 5.4, relativeVolatility: 114, status: 'OPEN' },
  { symbol: 'TSLA', side: 'LONG', hour: 13, minute: 18, durationMinutes: 32, quantity: 60, entryPrice: 184.8, pnl: 186, notes: 'Equity scalp followed a clean reclaim of the morning pivot.', tags: ['Momentum', 'Equity Scalp'], market: 'STOCKS', companyName: 'Tesla Inc.', strategy: 'Pivot Reclaim', atr: 7.6, relativeVolatility: 152, status: 'OPEN' },
  { symbol: 'META', side: 'SHORT', hour: 9, minute: 48, durationMinutes: 14, quantity: 25, entryPrice: 478.75, pnl: 96, notes: 'Quick scalp into first resistance after a failed high tick.', tags: ['Scalp', 'Social Media'], market: 'STOCKS', companyName: 'Meta Platforms Inc.', strategy: 'Resistance Fade', atr: 8.8, relativeVolatility: 129 },
  { symbol: 'AMZN', side: 'SHORT', hour: 10, minute: 29, durationMinutes: 24, quantity: 50, entryPrice: 183.4, pnl: -88, notes: 'Pullback entry did not follow through and was closed quickly.', tags: ['Discipline', 'E-commerce'], market: 'STOCKS', companyName: 'Amazon.com Inc.', strategy: 'Failed Fade', atr: 3.6, relativeVolatility: 94 },
  { symbol: 'JPM', side: 'SHORT', hour: 11, minute: 12, durationMinutes: 20, quantity: 35, entryPrice: 194.75, pnl: 312, notes: 'Trend reversal confirmed once buyers failed above overnight high.', tags: ['Reversal', 'Financials'], market: 'STOCKS', companyName: 'JPMorgan Chase & Co.', strategy: 'Failed Breakout', atr: 3.2, relativeVolatility: 102 },
  { symbol: 'NVDA', side: 'LONG', hour: 14, minute: 2, durationMinutes: 28, quantity: 18, entryPrice: 913.6, pnl: 74, notes: 'Small late-day continuation, scaled down to protect daily gains.', tags: ['Consistency', 'Semis', 'Long Term'], market: 'STOCKS', companyName: 'NVIDIA Corporation', strategy: 'Late Continuation', atr: 21.8, relativeVolatility: 143 },
  { symbol: 'NFLX', side: 'LONG', hour: 9, minute: 39, durationMinutes: 19, quantity: 20, entryPrice: 622.25, pnl: 260, notes: 'Strong reclaim through opening range high with clean breadth.', tags: ['Opening Drive', 'Streaming'], market: 'STOCKS', companyName: 'Netflix Inc.', strategy: 'Opening Drive', atr: 10.4, relativeVolatility: 147 },
  { symbol: 'GOOGL', side: 'SHORT', hour: 10, minute: 17, durationMinutes: 17, quantity: 45, entryPrice: 174.25, pnl: -140, notes: 'Pullback failed as price rotated back inside morning range.', tags: ['Scalp', 'Risk Cut'], market: 'STOCKS', companyName: 'Alphabet Inc.', strategy: 'Mean Reversion', atr: 3.1, relativeVolatility: 108 },
  { symbol: 'PLTR', side: 'LONG', hour: 12, minute: 6, durationMinutes: 39, quantity: 150, entryPrice: 24.2, pnl: 88, notes: 'Low-stress stock continuation during midday consolidation.', tags: ['Tech', 'Consistency'], market: 'STOCKS', companyName: 'Palantir Technologies', strategy: 'Midday Continuation', atr: 1.1, relativeVolatility: 78 },
  { symbol: 'CRM', side: 'LONG', hour: 13, minute: 45, durationMinutes: 16, quantity: 30, entryPrice: 231.5, pnl: 0, notes: 'Scratch exit after momentum stalled at prior high.', tags: ['Scratch', 'SaaS'], market: 'STOCKS', companyName: 'Salesforce Inc.', strategy: 'Breakout Attempt', atr: 5.6, relativeVolatility: 123 },
  { symbol: 'TSLA', side: 'LONG', hour: 9, minute: 36, durationMinutes: 21, quantity: 40, entryPrice: 181.5, pnl: -125, notes: 'First trade failed below VWAP; loss kept within daily risk.', tags: ['Risk Cut', 'Challenge'], market: 'STOCKS', companyName: 'Tesla Inc.', strategy: 'Opening Drive', atr: 7.6, relativeVolatility: 118 },
  { symbol: 'NVDA', side: 'SHORT', hour: 10, minute: 22, durationMinutes: 18, quantity: 15, entryPrice: 916.75, pnl: 350, notes: 'Best stock trade after failed high and sharp momentum shift.', tags: ['Momentum', 'Semis'], market: 'STOCKS', companyName: 'NVIDIA Corporation', strategy: 'Failed High', atr: 22.0, relativeVolatility: 153 },
  { symbol: 'AMD', side: 'SHORT', hour: 11, minute: 3, durationMinutes: 23, quantity: 50, entryPrice: 171.25, pnl: 78, notes: 'Scaled stock add-on after NVDA confirmed lower high.', tags: ['Scalp', 'Add-on'], market: 'STOCKS', companyName: 'Advanced Micro Devices', strategy: 'Lower High', atr: 5.2, relativeVolatility: 112 },
  { symbol: 'META', side: 'LONG', hour: 12, minute: 58, durationMinutes: 35, quantity: 35, entryPrice: 482.1, pnl: -62, notes: 'Choppy midday attempt, closed before daily drawdown limit.', tags: ['Discipline', 'Social Media'], market: 'STOCKS', companyName: 'Meta Platforms Inc.', strategy: 'Midday Breakout', atr: 8.5, relativeVolatility: 90 },
  { symbol: 'TSLA', side: 'SHORT', hour: 14, minute: 11, durationMinutes: 29, quantity: 50, entryPrice: 186.2, pnl: 152, notes: 'Late-day fade after failed high, partials taken into VWAP.', tags: ['Equity Scalp', 'Fade'], market: 'STOCKS', companyName: 'Tesla Inc.', strategy: 'Late Fade', atr: 7.9, relativeVolatility: 158 },
  { symbol: 'NVDA', side: 'SHORT', hour: 14, minute: 47, durationMinutes: 12, quantity: 12, entryPrice: 921.4, pnl: -58, notes: 'Small probe stopped when semis reclaimed the pivot.', tags: ['Semis', 'Risk Cut'], market: 'STOCKS', companyName: 'NVIDIA Corporation', strategy: 'Pivot Fade', atr: 22.5, relativeVolatility: 149 },
];

function buildPlans(seedPlans: DemoTradePlan[]) {
  const plans: DemoTradePlan[] = [];
  const total = TRADE_COUNTS_BY_DAY.reduce((sum, count) => sum + count, 0);

  for (let index = 0; index < total; index += 1) {
    plans.push(seedPlans[index % seedPlans.length]);
  }

  return plans;
}

export function generateDashboardDemoTrades(tenantId: DemoTenantId = 'trade-proof', referenceDate = new Date()): DemoTrade[] {
  const plans = buildPlans(tenantId === 'warrior-trading' ? WARRIOR_TRADING_PLANS : TRADE_PROOF_PLANS);
  const latestPlanMinute = plans.reduce((latest, plan) => Math.max(latest, (plan.hour * 60) + plan.minute + plan.durationMinutes), 0);
  const marketDateKeys = getRecentUsEquitySessionDateKeys(referenceDate, TRADE_COUNTS_BY_DAY.length, {
    latestRequiredMinute: latestPlanMinute,
  });
  let planIndex = 0;
  const trades: DemoTrade[] = [];

  TRADE_COUNTS_BY_DAY.forEach((count, dayIndex) => {
    const dateKey = marketDateKeys[dayIndex];
    if (!dateKey) throw new Error(`Missing demo market date for day index ${dayIndex}`);
    for (let sequence = 0; sequence < count; sequence += 1) {
      trades.push(makeDemoTrade(tenantId, dateKey, dayIndex, sequence, plans[planIndex]));
      planIndex += 1;
    }
  });

  return sortTradesNewestFirst(trades);
}

export function getDashboardDemoTradesForTenant(tenantId: string | null | undefined, referenceDate = new Date()) {
  const normalizedTenantId = String(tenantId || '').trim().toLowerCase() || 'trade-proof';
  return generateDashboardDemoTrades(normalizedTenantId, referenceDate);
}

export const DASHBOARD_DEMO_TRADES: DemoTrade[] = getDashboardDemoTradesForTenant('trade-proof');
export const WARRIOR_DASHBOARD_DEMO_TRADES: DemoTrade[] = getDashboardDemoTradesForTenant('warrior-trading');
